import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  CreateLeadRequest,
  CreateLeadResponse,
  LeadResult,
  UpdateLeadRequest,
  UserToken,
} from '@myfaro/domain';
import { ApiError, useFetch } from '../core/api';
import { useAuth } from '../core/auth';
import { buildQueryString } from './helpers';
import { useAppContext } from '../core/context';

// Creates a new Lead object. This corresponds to a CaseFile in myFaro which holds a single Party.
// The unique identifier for a new Lead is its Lead ID.
export const useCreateLead = () => {
  const {
    appConfig: { broker_uid },
  } = useAppContext();
  const auth = useAuth();
  const fetch = useFetch();
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading, error } = useMutation<
    CreateLeadResponse,
    ApiError,
    any
  >(
    values =>
      fetch
        .post('leads', {
          skipAuthentication: true,
          body: {
            brokerUid: broker_uid,
            leadInput: values,
          } as any,
        })
        .then((res: CreateLeadResponse) => res),
    {
      onSuccess(createLeadResponse) {
        if (createLeadResponse.userToken != null) {
          auth.setUserToken({
            accessToken: createLeadResponse.userToken,
          });
          queryClient.setQueryData(['lead'], createLeadResponse.leadResult);
        }
      },
    },
  );

  return {
    createLead: mutateAsync,
    isLoading,
    error,
  };
};

// Gets the details for a Lead using the myFaro uid (which is encoded in the JSON Web Token).
export const useLead = () => {
  const {
    appConfig: { broker_uid },
  } = useAppContext();
  const auth = useAuth();
  const fetch = useFetch();

  const { data, isLoading, error } = useQuery<LeadResult, ApiError>(
    ['lead', auth.getAccessToken()],
    () =>
      fetch
        .get(
          `leads${buildQueryString({
            broker_uid,
            leadUid: auth.getAccessToken(),
          })}`,
        )
        .then((res: LeadResult) => {
          return res;
        }),
  );

  return {
    lead: data,
    error,
    isLoading,
  };
};

// Updates the Lead object with the myFaro uid encoded in the JSON Web Token.
export const useUpdateLead = () => {
  const {
    appConfig: { broker_uid },
  } = useAppContext();
  const auth = useAuth();
  const fetch = useFetch();
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading, error } = useMutation<
    CreateLeadResponse,
    ApiError,
    any
  >(
    values =>
      fetch
        .put('leads', {
          body: {
            brokerUid: broker_uid,
            leadInput: values,
          } as any,
        })
        .then((res: CreateLeadResponse) => res),
    {
      onSuccess(leadResult) {
        queryClient.setQueryData(['lead'], leadResult);
      },
    },
  );

  return {
    updateLead: mutateAsync,
    isLoading,
    error,
  };
};

// Notifies the user corresponding to the myFaro uid encoded in
// the JSON Web Token and send them the results again.
export const useLeadNotify = () => {
  const {
    appConfig: { broker_uid },
  } = useAppContext();
  const auth = useAuth();
  const fetch = useFetch();

  const { mutateAsync, isLoading, error, isSuccess } = useMutation<
    undefined,
    ApiError
  >(
    () =>
      fetch
        .put(
          `leads/notify${buildQueryString({
            brokerUid: broker_uid,
            leadUid: auth.getAccessToken(),
          })}`,
        )
        .then((res: undefined) => res),
    {},
  );

  return {
    notify: mutateAsync,
    isSuccess,
    isLoading,
    error,
  };
};

// Refreshes the user token.
export const useRefreshToken = () => {
  const {
    appConfig: { broker_uid },
  } = useAppContext();
  const fetch = useFetch();

  const { mutateAsync, isLoading, error, isSuccess } = useMutation<
    UserToken,
    ApiError,
    string
  >(['userToken'], (email: string) =>
    fetch
      .put(
        `leads/refreshUserToken${buildQueryString({
          brokerUid: broker_uid,
          email,
        })}`,
        {
          skipAuthentication: true,
        },
      )
      .then((res: UserToken) => res),
  );

  return {
    refreshUserToken: mutateAsync,
    isSuccess,
    isLoading,
    error,
  };
};
